import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { LoanOfficer } from '../data/mockData';
import { formatCurrency, formatPercent, getPerformanceColor } from '../utils/helpers';
import { DateRange, isCountMode } from './DashboardHeader';
interface LoanOfficerCardsProps {
  loanOfficers: LoanOfficer[];
  onSelectLO: (lo: LoanOfficer) => void;
  dateRange: DateRange;
}
export function LoanOfficerCards({
  loanOfficers,
  onSelectLO,
  dateRange
}: LoanOfficerCardsProps) {
  const showCounts = isCountMode(dateRange);
  const getStatusBadge = (lo: LoanOfficer) => {
    if (lo.coachingFlags.length > 0) {
      return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-86">
          Needs Coaching
        </span>;
    }
    // Simple heuristic for warning vs strong
    const yellowCount = [getPerformanceColor('five9CallsPerDay', lo.five9CallsPerDay), getPerformanceColor('ringCentralCallsPerDay', lo.ringCentralCallsPerDay), getPerformanceColor('pullToAppPct', lo.pullToAppPct), getPerformanceColor('falloutPct', lo.falloutPct)].filter(c => c === 'yellow').length;
    if (yellowCount >= 2) {
      return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-87">
          Warning
        </span>;
    }
    return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-88">
        Strong
      </span>;
  };
  const MetricItem = ({
    label,
    value,
    metricKey,
    isPercent = false
  }: {
    label: string;
    value: number;
    metricKey: string;
    isPercent?: boolean;
  }) => {
    const color = getPerformanceColor(metricKey, value);
    const dotColor = color === 'green' ? 'bg-green-500' : color === 'yellow' ? 'bg-amber-500' : 'bg-red-500';
    const displayValue = isPercent ? formatPercent(value) : value.toString();
    return <div className="flex justify-between items-center py-1" data-id="element-89">
        <span className="text-xs text-gray-500" data-id="element-90">{label}</span>
        <div className="flex items-center gap-1.5" data-id="element-91">
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} data-id="element-92"></span>
          <span className="text-sm font-medium text-gray-900" data-id="element-93">
            {displayValue}
          </span>
        </div>
      </div>;
  };
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-94">
      {loanOfficers.map(lo => {
      const isFlagged = lo.coachingFlags.length > 0;
      return <div key={lo.id} onClick={() => onSelectLO(lo)} className={`bg-white rounded-xl shadow-sm p-5 cursor-pointer transition-all hover:shadow-md border border-gray-100 ${isFlagged ? 'border-l-4 border-l-red-500' : ''}`} data-id="element-95">
            <div className="flex justify-between items-start mb-4" data-id="element-96">
              <div className="flex items-center gap-3" data-id="element-97">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold" data-id="element-98">
                  {lo.initials}
                </div>
                <div data-id="element-99">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1" data-id="element-100">
                    {lo.name}
                    {isFlagged && <AlertTriangle className="w-3.5 h-3.5 text-red-500" data-id="element-101" />}
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-102">{lo.team}</p>
                </div>
              </div>
              {getStatusBadge(lo)}
            </div>

            <div className="mb-4" data-id="element-103">
              <p className="text-xs text-gray-500 mb-1" data-id="element-104">Pipeline Value</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-105">
                {formatCurrency(lo.pipelineValue)}
              </p>
            </div>

            <div className="space-y-1 bg-gray-50 p-3 rounded-lg" data-id="element-106">
              <MetricItem label="Five9 Calls/Day" value={lo.five9CallsPerDay} metricKey="five9CallsPerDay" data-id="element-107" />
              <MetricItem label="RC Calls/Day" value={lo.ringCentralCallsPerDay} metricKey="ringCentralCallsPerDay" data-id="element-108" />
              <MetricItem label="Total Calls/Day" value={lo.five9CallsPerDay + lo.ringCentralCallsPerDay} metricKey="totalCallsPerDay" data-id="element-109" />
              {showCounts ? <>
                  <MetricItem label="Credit Pulls" value={lo.creditPulls} metricKey="" data-id="element-110" />
                  <MetricItem label="Applications" value={lo.applications} metricKey="" data-id="element-111" />
                  <MetricItem label="Submissions" value={lo.submissions} metricKey="" data-id="element-112" />
                  <MetricItem label="Signed" value={lo.signed} metricKey="" data-id="element-113" />
                  <MetricItem label="Locks" value={lo.locks} metricKey="" data-id="element-114" />
                  <MetricItem label="Fundings" value={lo.fundings} metricKey="" data-id="element-115" />
                </> : <>
                  <MetricItem label="Pull → App" value={lo.pullToAppPct} metricKey="pullToAppPct" isPercent data-id="element-116" />
                  <MetricItem label="Fallout" value={lo.falloutPct} metricKey="falloutPct" isPercent data-id="element-117" />
                  <MetricItem label="Follow-Up" value={lo.followUpCompliancePct} metricKey="followUpCompliancePct" isPercent data-id="element-118" />
                </>}
            </div>
          </div>;
    })}
    </div>;
}